import { useContext, useMemo } from 'react'
import PropTypes from 'prop-types'
import merge from 'lodash/merge'
import { TemplateProvider } from '@siteplus/ai-templates'
import { OnboardingContext } from './OnboardingContext'
import { useTemplateContext } from './TemplateProvider'
import { useTemplateColors } from '@/hooks/useOnboarding'
import { TEMPLATE_VERSIONS } from '@/constants/versions'
import { buildPaletteFromSwatch } from '@/helpers/buildPaletteFromSwatch'
import useAIConfig from '@/hooks/useAIConfig'
import useConfigOverrides from '@/hooks/useConfigOverrides'

const PRE_COLOR_KEYS = ['brand', 'altBrand', 'text', 'textAlt', 'complementary']

function paletteHasCompletePreColor(palette) {
  const pc = palette?.styles?.preColor
  if (!pc || typeof pc !== 'object') return false
  return PRE_COLOR_KEYS.every(
    (k) => pc[k] != null && String(pc[k]).trim() !== '',
  )
}

/** Paths used by grid `/pages`-style payloads, home, and per-page blocks (see templateConfigMappings). */
const ACCOUNT_INFO_PATHS = [
  'pages.home.blocks.accountInfo',
  'home.blocks.accountInfo',
  'contact.blocks.accountInfo',
  'pages.contact.blocks.accountInfo',
  'aboutus.blocks.accountInfo',
  'pages.aboutus.blocks.accountInfo',
]

function accountInfoFromAiConfig(get) {
  for (const path of ACCOUNT_INFO_PATHS) {
    const v = get(path, null)
    if (v && typeof v === 'object') return v
  }
  return null
}

function accountFieldContent(accountInfo, key) {
  if (!accountInfo || typeof accountInfo !== 'object') return undefined
  const node = accountInfo[key]
  if (node == null) return undefined
  if (typeof node === 'object' && 'content' in node) {
    const c = node.content
    if (c == null || (typeof c === 'string' && c.trim() === '')) return undefined
    return typeof c === 'string' ? c : String(c)
  }
  if (typeof node === 'string' && node.trim() !== '') return node
  return undefined
}

/** Same shape as `templateConfigMappings` siteFooter + Law Zip `contact` props when home does not pass them. */
function footerInjectionsFromAccountInfo(accountInfo) {
  if (!accountInfo) return { siteFooter: null, contact: null }
  const email = accountFieldContent(accountInfo, 'email')
  const phone = accountFieldContent(accountInfo, 'phone')
  const address = accountFieldContent(accountInfo, 'address')
  if (!email && !phone && !address) return { siteFooter: null, contact: null }
  /** Nested keys for Max Fit / Pet Sitter; top-level phone/email/address for Shampoo Conditioner footer merge. */
  const siteFooter = {
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
    ...(address ? { address } : {}),
    contact: {
      ...(email ? { email } : {}),
      ...(phone ? { phone } : {}),
    },
    visit: {
      ...(address ? { address } : {}),
    },
  }
  const contact = {
    ...(email ? { email } : {}),
    ...(phone ? { phone } : {}),
    ...(address ? { address } : {}),
  }
  return { siteFooter, contact }
}

/**
 * Bridge component that connects OnboardingContext to the shared @siteplus/ai-templates TemplateProvider.
 * This provides templates with access to styles, templateName, layout, visibility, etc.
 */
const SharedTemplateProvider = ({ children }) => {
  const {
    selectedPalette,
    selectedTemplate,
    layout,
    recommendedApps,
    sections,
    classicMenu,
    templateStyle,
    defaultTemplatesVersion,
    isGrid,
  } = useContext(OnboardingContext)

  const { template } = useTemplateContext()
  const { data: templateColorsData } = useTemplateColors(selectedTemplate?.id)
  const { get } = useAIConfig()
  /** `siteFooter.*` mappings live under `header_and_introduction` in templateConfigMappings. */
  const headerIntroOverrides = useConfigOverrides('header_and_introduction')

  const accountInfo = useMemo(() => accountInfoFromAiConfig(get), [get])

  const footerInjections = useMemo(
    () => footerInjectionsFromAccountInfo(accountInfo),
    [accountInfo],
  )

  /**
   * Footers use `template.siteFooter` / `template.contact` when inner pages render `<Footer />` without props.
   * Prefer mapping-driven `headerIntroOverrides` (home.blocks paths); fill gaps from `accountInfo` so grid
   * (`pages.home.blocks.accountInfo`) still injects contact rows without duplicating every mapping path.
   */
  const templateWithSiteFooter = useMemo(() => {
    if (!template) return template
    const sfOverride = headerIntroOverrides?.siteFooter
    const ctOverride = headerIntroOverrides?.contact
    const baseSf = footerInjections.siteFooter
    const baseCt = footerInjections.contact

    if (
      (!sfOverride || typeof sfOverride !== 'object') &&
      (!ctOverride || typeof ctOverride !== 'object') &&
      (!baseSf || typeof baseSf !== 'object') &&
      (!baseCt || typeof baseCt !== 'object')
    ) {
      return template
    }

    const next = { ...template }
    if (
      (baseSf && typeof baseSf === 'object') ||
      (sfOverride && typeof sfOverride === 'object')
    ) {
      next.siteFooter = merge(
        {},
        template.siteFooter || {},
        baseSf || {},
        sfOverride || {},
      )
    }
    if (
      (baseCt && typeof baseCt === 'object') ||
      (ctOverride && typeof ctOverride === 'object')
    ) {
      next.contact = merge(
        {},
        template.contact || {},
        baseCt || {},
        ctOverride || {},
      )
    }
    return next
  }, [template, headerIntroOverrides, footerInjections])

  const paletteForPreview = useMemo(() => {
    if (paletteHasCompletePreColor(selectedPalette)) {
      return selectedPalette
    }

    const palettes = Array.isArray(templateColorsData) ? templateColorsData : []
    if (palettes.length > 0) {
      const idx = Math.min(Math.max(0, layout), palettes.length - 1)
      const byLayout = palettes[idx]
      if (paletteHasCompletePreColor(byLayout)) {
        return byLayout
      }
    }

    const templateVersion = selectedTemplate?.version ?? defaultTemplatesVersion
    const defaultSwatch =
      TEMPLATE_VERSIONS(isGrid)?.[templateStyle]?.[templateVersion]?.defaultSwatch
    if (defaultSwatch) {
      return buildPaletteFromSwatch('preview-default', defaultSwatch, 'preview')
    }

    return selectedPalette ?? null
  }, [
    selectedPalette,
    templateColorsData,
    layout,
    templateStyle,
    selectedTemplate,
    defaultTemplatesVersion,
    isGrid,
  ])

  const HeroComponent = selectedTemplate?.templates?.[layout]?.template

  return (
    <TemplateProvider
      palette={paletteForPreview}
      templateName={selectedTemplate?.name}
      layout={layout}
      recommendedApps={recommendedApps}
      template={templateWithSiteFooter}
      sections={sections}
      classicMenu={classicMenu}
      HeroComponent={HeroComponent}
      accountInfo={accountInfo}
    >
      {children}
    </TemplateProvider>
  )
}

SharedTemplateProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export default SharedTemplateProvider
